import React, { useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { FileUp, Sparkles, X, AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import Modal from '../Modal';
import Button from '../Button';
import { aiApi, aiUpload, formatScore, scoreColor, AI_ACCEPT } from '../../utils/aiApi';

// Supplier document (invoice, delivery note…) → order items of one batch. Claude reads it and suggests pairs;
// the user checks every line and nothing is saved until "Použít".

const ACCENT = '#E53935';
const BORDER = '#E0E0E0';
const MAX_QTY = 10000;
// Same threshold as the server: a supplier is preset only when the name match is this sure.
const SUPPLIER_PRESET_SCORE = 0.6;
const MOBILE_QUERY = '(max-width: 640px)';

const DOC_TYPES = {
  invoice: 'Faktura',
  delivery_note: 'Dodací list',
  order_confirmation: 'Potvrzení objednávky',
  quote: 'Nabídka',
  other: 'Jiný doklad',
};

const ACTIONS = [
  { value: 'deliver', label: 'Označit jako dodané' },
  { value: 'add', label: 'Přidat do objednávky' },
  { value: 'skip', label: 'Přeskočit' },
];

const inputStyle = {
  width: '100%', padding: '8px 10px', border: `1px solid ${BORDER}`, borderRadius: '8px',
  fontSize: '14px', background: '#fff', boxSizing: 'border-box', fontFamily: 'inherit',
};
const labelStyle = { fontSize: '12px', fontWeight: 600, color: '#666', display: 'block', marginBottom: '4px' };

function subscribeMobile(callback) {
  const mq = window.matchMedia(MOBILE_QUERY);
  mq.addEventListener('change', callback);
  return () => mq.removeEventListener('change', callback);
}
const useIsMobile = () => useSyncExternalStore(subscribeMobile, () => window.matchMedia(MOBILE_QUERY).matches, () => false);

const partLabel = (p) => (p.model ? `${p.name} (${p.model})` : p.name);
const money = (n, currency) => (n === null || n === undefined ? '' : `${Number(n).toLocaleString('cs-CZ', { maximumFractionDigits: 2 })}${currency ? ` ${currency}` : ''}`);

async function getList(url) {
  const res = await fetch(url, { credentials: 'include' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || !json.success) throw new Error(json.error || `Chyba ${res.status}`);
  return json.data || [];
}

function initialRow(line) {
  const p = line.proposal || {};
  const action = p.action === 'deliver' || p.action === 'add' ? p.action : 'skip';
  const partId = p.partId || line.partMatches?.[0]?.id || '';
  return { partId: partId ? String(partId) : '', action, qty: String(p.qty || 1), alreadyDelivered: p.action === 'already_delivered' };
}

// For each row that delivers: the first undelivered item of the batch with that part not taken by an earlier row.
function resolveDeliveries(rows, items) {
  const taken = new Set();
  return rows.map((row) => {
    if (row.action !== 'deliver' || !row.partId) return null;
    const item = items.find((i) => String(i.part_id) === row.partId && !i.delivered_at && !taken.has(i.id));
    if (!item) return null;
    taken.add(item.id);
    return item;
  });
}

function rowError(row, target) {
  if (row.action === 'skip') return null;
  if (!row.partId) return 'Vyberte díl.';
  if (row.action === 'deliver' && !target) return 'Tento díl v objednávce nečeká na dodání.';
  if (row.action === 'add') {
    const qty = Number(row.qty);
    if (!Number.isInteger(qty) || qty < 1 || qty > MAX_QTY) return `Množství musí být celé číslo 1–${MAX_QTY}.`;
  }
  return null;
}

function ScoreBadge({ match }) {
  if (!match) return null;
  return (
    <span
      title={match.reasons?.length ? `Shoda: ${match.reasons.join(', ')}` : 'Návrh AI'}
      style={{ fontSize: '11px', fontWeight: 700, color: '#fff', background: scoreColor(match.score), borderRadius: '10px', padding: '2px 8px', whiteSpace: 'nowrap' }}
    >
      {formatScore(match.score)}
    </span>
  );
}

function PartSelect({ line, row, parts, onChange }) {
  const suggested = new Set(line.partMatches.map((m) => String(m.id)));
  const others = parts.filter((p) => !suggested.has(String(p.id)));
  return (
    <select value={row.partId} onChange={(e) => onChange({ partId: e.target.value })} style={inputStyle} aria-label="Díl">
      <option value="">— bez dílu —</option>
      {line.partMatches.length > 0 && (
        <optgroup label="Návrhy AI">
          {line.partMatches.map((m) => <option key={m.id} value={String(m.id)}>{`${m.label} · ${formatScore(m.score)}`}</option>)}
        </optgroup>
      )}
      <optgroup label="Všechny díly">
        {others.map((p) => <option key={p.id} value={String(p.id)}>{partLabel(p)}</option>)}
      </optgroup>
    </select>
  );
}

function lineFields({ line, row, target, parts, onChange, currency }) {
  const match = line.partMatches.find((m) => String(m.id) === row.partId);
  const error = rowError(row, target);
  return {
    info: (
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: '14px', color: '#333', overflowWrap: 'anywhere' }}>{line.description}</div>
        <div style={{ fontSize: '12px', color: '#777', marginTop: '2px' }}>
          {line.code && <span>Kód: {line.code} · </span>}
          {line.qty} {line.unit || 'ks'}
          {line.unitPrice !== null && <span> · {money(line.unitPrice, currency)}/ks</span>}
        </div>
        {row.alreadyDelivered && <div style={{ fontSize: '12px', color: '#2E7D32', marginTop: '2px' }}>Už je v objednávce jako dodané</div>}
        {error && <div style={{ fontSize: '12px', color: ACCENT, marginTop: '2px' }}>{error}</div>}
      </div>
    ),
    part: (
      <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
        <div style={{ flex: 1, minWidth: 0 }}><PartSelect line={line} row={row} parts={parts} onChange={onChange} /></div>
        <ScoreBadge match={match} />
      </div>
    ),
    action: (
      <select value={row.action} onChange={(e) => onChange({ action: e.target.value })} style={inputStyle} aria-label="Akce">
        {ACTIONS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
      </select>
    ),
    qty: (
      <input
        type="number" min="1" max={MAX_QTY} step="1" inputMode="numeric" aria-label="Množství"
        value={row.action === 'deliver' && target ? String(target.qty) : row.qty}
        disabled={row.action !== 'add'}
        onChange={(e) => onChange({ qty: e.target.value })}
        style={{ ...inputStyle, opacity: row.action === 'add' ? 1 : 0.6 }}
      />
    ),
  };
}

function LinesView({ lines, rows, targets, parts, onRowChange, currency, isMobile }) {
  if (!lines.length) return null;
  if (isMobile) {
    return (
      <div style={{ display: 'grid', gap: '10px' }}>
        {lines.map((line) => {
          const f = lineFields({ line, row: rows[line.index], target: targets[line.index], parts, currency, onChange: (patch) => onRowChange(line.index, patch) });
          return (
            <div key={line.index} style={{ border: `1px solid ${BORDER}`, borderRadius: '12px', padding: '12px', display: 'grid', gap: '8px' }}>
              {f.info}
              <div><span style={labelStyle}>Díl</span>{f.part}</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 90px', gap: '8px' }}>
                <div><span style={labelStyle}>Akce</span>{f.action}</div>
                <div><span style={labelStyle}>Ks</span>{f.qty}</div>
              </div>
            </div>
          );
        })}
      </div>
    );
  }
  const th = { textAlign: 'left', fontSize: '12px', color: '#666', fontWeight: 600, padding: '8px', borderBottom: `1px solid ${BORDER}` };
  const td = { padding: '8px', borderBottom: '1px solid #F0F0F0', verticalAlign: 'top' };
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
      <thead>
        <tr>
          <th style={{ ...th, width: '32%' }}>Položka dokladu</th>
          <th style={{ ...th, width: '34%' }}>Díl</th>
          <th style={{ ...th, width: '22%' }}>Akce</th>
          <th style={{ ...th, width: '12%' }}>Ks</th>
        </tr>
      </thead>
      <tbody>
        {lines.map((line) => {
          const f = lineFields({ line, row: rows[line.index], target: targets[line.index], parts, currency, onChange: (patch) => onRowChange(line.index, patch) });
          return (
            <tr key={line.index}>
              <td style={td}>{f.info}</td>
              <td style={td}>{f.part}</td>
              <td style={td}>{f.action}</td>
              <td style={td}>{f.qty}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function DocumentSummary({ document }) {
  const parts = [
    DOC_TYPES[document.type] || 'Doklad',
    document.number && `č. ${document.number}`,
    document.date && new Date(document.date).toLocaleDateString('cs-CZ'),
    document.currency,
  ].filter(Boolean);
  return (
    <div style={{ background: '#FAFAFA', border: `1px solid ${BORDER}`, borderRadius: '12px', padding: '12px', fontSize: '14px' }}>
      <div style={{ fontWeight: 600, color: '#333' }}>{parts.join(' · ')}</div>
      <div style={{ color: '#666', marginTop: '2px' }}>
        Dodavatel: {document.supplier?.name || 'nerozpoznán'}{document.supplier?.ico ? ` (IČO ${document.supplier.ico})` : ''}
      </div>
    </div>
  );
}

function ImportPanel({ batchId, onClose, onApplied }) {
  const isMobile = useIsMobile();
  const [file, setFile] = useState(null);
  const [phase, setPhase] = useState('pick'); // pick | loading | review | applying | done
  const [error, setError] = useState('');
  const [analysis, setAnalysis] = useState(null);
  const [rows, setRows] = useState([]);
  const [supplierId, setSupplierId] = useState('');
  const [showServices, setShowServices] = useState(false);
  const [lists, setLists] = useState({ parts: [], suppliers: [], items: [], loaded: false });
  const [applied, setApplied] = useState(null);

  useEffect(() => {
    let alive = true;
    Promise.all([
      getList('/api/parts?limit=500'),
      getList('/api/suppliers?limit=500'),
      getList(`/api/orders/${batchId}/items`),
    ])
      .then(([parts, suppliers, items]) => { if (alive) setLists({ parts, suppliers, items, loaded: true }); })
      .catch((e) => { if (alive) setError(`Nepodařilo se načíst díly a dodavatele: ${e.message}`); });
    return () => { alive = false; };
  }, [batchId]);

  const targets = useMemo(() => resolveDeliveries(rows, lists.items), [rows, lists.items]);

  const analyze = async () => {
    if (!file) return;
    setPhase('loading');
    setError('');
    try {
      const data = await aiUpload(`/orders/${batchId}/analyze`, file);
      setAnalysis(data);
      setRows(data.lines.map(initialRow));
      const best = data.supplierMatches?.[0];
      setSupplierId(best && best.score >= SUPPLIER_PRESET_SCORE ? String(best.id) : '');
      setPhase('review');
    } catch (e) {
      setError(e.message);
      setPhase('pick');
    }
  };

  const updateRow = (index, patch) => setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));

  const chosen = rows.map((row, i) => ({ row, target: targets[i] })).filter(({ row }) => row.action !== 'skip');
  const invalid = chosen.some(({ row, target }) => rowError(row, target));

  const apply = async () => {
    const actions = chosen.map(({ row, target }) => (row.action === 'deliver'
      ? { action: 'deliver', orderItemId: target.id }
      : { action: 'add', partId: Number(row.partId), supplierId: supplierId ? Number(supplierId) : null, qty: Number(row.qty), note: '' }));
    setPhase('applying');
    setError('');
    try {
      const result = await aiApi(`/orders/${batchId}/apply`, { method: 'POST', body: { token: analysis.token, actions } });
      setApplied(result);
      setPhase('done');
      if (onApplied) onApplied(result);
    } catch (e) {
      setError(e.message);
      setPhase('review');
    }
  };

  const goods = analysis ? analysis.lines.filter((l) => !l.isService) : [];
  const services = analysis ? analysis.lines.filter((l) => l.isService) : [];
  const needsSupplier = rows.some((r) => r.action === 'add');
  const suggestedSuppliers = analysis?.supplierMatches || [];
  const otherSuppliers = lists.suppliers.filter((s) => !suggestedSuppliers.some((m) => m.id === s.id));
  const currency = analysis?.document?.currency;

  return (
    <div
      role="dialog" aria-modal="true" aria-labelledby="ai-order-import-title"
      style={{ background: '#fff', borderRadius: '12px', maxWidth: '900px', width: isMobile ? '100%' : 'calc(100% - 32px)', maxHeight: '90vh', overflow: 'auto', padding: isMobile ? '16px' : '24px', boxSizing: 'border-box' }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', marginBottom: '16px' }}>
        <div>
          <h2 id="ai-order-import-title" style={{ margin: 0, fontSize: '18px', color: '#333' }}>Načíst doklad dodavatele</h2>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', marginTop: '6px', fontSize: '12px', fontWeight: 600, color: ACCENT, background: '#FFEBEE', borderRadius: '10px', padding: '2px 8px' }}>
            <Sparkles size={12} /> Zpracuje Claude
          </span>
        </div>
        <button type="button" onClick={onClose} aria-label="Zavřít" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: '#666' }}>
          <X size={20} />
        </button>
      </div>

      {error && (
        <div role="alert" style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', background: '#FFEBEE', color: '#C62828', borderRadius: '8px', padding: '10px 12px', fontSize: '14px', marginBottom: '12px' }}>
          <AlertTriangle size={18} style={{ flexShrink: 0 }} /> <span>{error}</span>
        </div>
      )}

      {(phase === 'pick' || phase === 'loading') && (
        <div style={{ display: 'grid', gap: '12px' }}>
          <p style={{ margin: 0, fontSize: '14px', color: '#555' }}>
            Nahrajte fakturu, dodací list, potvrzení objednávky nebo nabídku (PDF nebo fotografii). Claude přečte položky a navrhne,
            které díly označit jako dodané a které do objednávky přidat. Nic se neuloží, dokud návrhy nepotvrdíte.
          </p>
          <label style={{ display: 'flex', alignItems: 'center', gap: '10px', border: `1px dashed ${BORDER}`, borderRadius: '12px', padding: '16px', cursor: 'pointer', fontSize: '14px', color: '#333' }}>
            <FileUp size={20} style={{ color: ACCENT, flexShrink: 0 }} />
            <span style={{ overflowWrap: 'anywhere' }}>{file ? file.name : 'Vybrat soubor…'}</span>
            <input type="file" accept={AI_ACCEPT} onChange={(e) => setFile(e.target.files?.[0] || null)} disabled={phase === 'loading'} style={{ display: 'none' }} />
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <Button onClick={analyze} disabled={!file} loading={phase === 'loading'} icon={Sparkles}>Načíst doklad</Button>
            {phase === 'loading' && <span style={{ fontSize: '14px', color: '#666' }}>Claude čte doklad…</span>}
          </div>
        </div>
      )}

      {(phase === 'review' || phase === 'applying') && analysis && (
        <div style={{ display: 'grid', gap: '14px' }}>
          <DocumentSummary document={analysis.document} />
          <div style={{ fontSize: '13px', color: '#795548', background: '#FFF8E1', borderRadius: '8px', padding: '8px 12px' }}>
            Návrhy připravila AI – zkontrolujte díl, akci a množství u každé položky.
          </div>

          {goods.length === 0 && <div style={{ fontSize: '14px', color: '#666' }}>Na dokladu nebyly nalezeny žádné položky zboží.</div>}
          <LinesView lines={goods} rows={rows} targets={targets} parts={lists.parts} onRowChange={updateRow} currency={currency} isMobile={isMobile} />

          {services.length > 0 && (
            <div>
              <button type="button" onClick={() => setShowServices((v) => !v)} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: '13px', color: '#666' }}>
                {showServices ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                Služby, doprava a slevy ({services.length}) – ve výchozím stavu přeskočeno
              </button>
              {showServices && (
                <div style={{ marginTop: '8px' }}>
                  <LinesView lines={services} rows={rows} targets={targets} parts={lists.parts} onRowChange={updateRow} currency={currency} isMobile={isMobile} />
                </div>
              )}
            </div>
          )}

          {needsSupplier && (
            <label style={{ display: 'block', maxWidth: '420px' }}>
              <span style={labelStyle}>Dodavatel pro přidané položky</span>
              <select value={supplierId} onChange={(e) => setSupplierId(e.target.value)} style={inputStyle}>
                <option value="">— bez dodavatele —</option>
                {suggestedSuppliers.length > 0 && (
                  <optgroup label="Návrhy AI">
                    {suggestedSuppliers.map((m) => <option key={m.id} value={String(m.id)}>{`${m.label} · ${formatScore(m.score)}`}</option>)}
                  </optgroup>
                )}
                <optgroup label="Všichni dodavatelé">
                  {otherSuppliers.map((s) => <option key={s.id} value={String(s.id)}>{s.name}</option>)}
                </optgroup>
              </select>
            </label>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '8px', flexWrap: 'wrap', borderTop: `1px solid ${BORDER}`, paddingTop: '14px' }}>
            <span style={{ fontSize: '13px', color: '#666', marginRight: 'auto' }}>
              Dodáno: {chosen.filter((c) => c.row.action === 'deliver').length} · Přidat: {chosen.filter((c) => c.row.action === 'add').length}
            </span>
            <Button variant="secondary" onClick={onClose} disabled={phase === 'applying'}>Zrušit</Button>
            <Button onClick={apply} disabled={chosen.length === 0 || invalid} loading={phase === 'applying'}>Použít</Button>
          </div>
        </div>
      )}

      {phase === 'done' && applied && (
        <div style={{ display: 'grid', gap: '14px' }}>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', background: '#E8F5E9', color: '#2E7D32', borderRadius: '12px', padding: '14px', fontSize: '14px', fontWeight: 600 }}>
            <CheckCircle2 size={20} style={{ flexShrink: 0 }} />
            Hotovo – označeno jako dodané: {applied.delivered}, přidáno do objednávky: {applied.added}.
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={onClose}>Zavřít</Button>
          </div>
        </div>
      )}

      {phase === 'pick' && !lists.loaded && !error && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#999', marginTop: '10px' }}>
          <Loader2 size={12} className="animate-spin" /> Načítám díly…
        </div>
      )}
    </div>
  );
}

export default function AiOrderImport({ batchId, open, onClose, onApplied }) {
  return (
    // no backdrop close: a stray click must not throw away a reviewed document
    <Modal show={open}>
      {open && <ImportPanel key={batchId} batchId={batchId} onClose={onClose} onApplied={onApplied} />}
    </Modal>
  );
}
